import { useState } from 'react';
import {
  useConnectCloverMutation,
  useDisconnectPosMutation,
  useGetPosConnectionsQuery,
  useSyncPosMenuMutation,
  type PosConnectionRecord
} from '@/store/accountApi';
import { useActiveLocation } from '@/hooks/useActiveLocation';
import { useAppDispatch } from '@/store';
import { pushToast } from '@/store/uiSlice';
import { Button, EmptyState, Field, Panel, SkeletonRows, Status } from '@/components/ui';
import { formatDateTime } from '@/utils/format';
import { PosStatusBanner } from './PosStatusBanner';

/** Providers with a service behind them. The rest are architecture-only today. */
const PROVIDERS = [
  { code: 'CLOVER', name: 'Clover', available: true },
  { code: 'TOAST', name: 'Toast', available: false },
  { code: 'SQUARE', name: 'Square', available: false },
  { code: 'SKYTAB', name: 'SkyTab', available: false },
  { code: 'LIGHTSPEED', name: 'Lightspeed', available: false }
];

export function PosPage() {
  const { locationId, locations } = useActiveLocation();
  const dispatch = useAppDispatch();

  const connections = useGetPosConnectionsQuery();
  const [connect, connectState] = useConnectCloverMutation();
  const [sync, syncState] = useSyncPosMenuMutation();
  const [disconnect] = useDisconnectPosMutation();

  const [form, setForm] = useState({ merchantId: '', accessToken: '' });

  const submit = async () => {
    if (!form.merchantId.trim() || !form.accessToken.trim()) {
      dispatch(pushToast('Both the merchant ID and the token are needed.', 'error'));
      return;
    }

    const result = await connect({ locationId, ...form });
    if ('error' in result) {
      dispatch(pushToast('Clover rejected those details.', 'error'));
      return;
    }

    setForm({ merchantId: '', accessToken: '' });

    if (!result.data.connected) {
      dispatch(pushToast('Saved, but Clover did not respond. Check the token.', 'error'));
      return;
    }

    const name = result.data.merchantName ?? 'Clover';
    dispatch(
      pushToast(
        result.data.synced
          ? `Connected to ${name} — pulled ${result.data.synced.items} menu items.`
          : `Connected to ${name}. Run a menu sync to pull the catalogue.`,
        'success'
      )
    );
  };

  const runSync = async (connection: PosConnectionRecord) => {
    if (!connection.connectionId) return;
    const result = await sync(connection.connectionId);
    dispatch(
      'error' in result
        ? pushToast('Menu sync failed. Check the connection and try again.', 'error')
        : pushToast(`Brought across ${result.data.items} items.`, 'success')
    );
  };

  const drop = async (connection: PosConnectionRecord) => {
    if (!connection.connectionId) return;
    const result = await disconnect(connection.connectionId);
    dispatch(
      'error' in result
        ? pushToast('Could not disconnect.', 'error')
        : pushToast('Disconnected. Orders will no longer be sent.', 'success')
    );
  };

  // Every location is returned; only those with a connection id are wired up.
  const rows = connections.data?.data ?? [];
  const connected = rows.filter((row) => row.connectionId);
  const notConnected = rows.filter((row) => !row.connectionId);

  return (
    <div className="space-y-4">
      <PosStatusBanner />

      <h1 className="text-display">Point of sale</h1>
      <p className="max-w-2xl text-small text-slate">
        Connecting your POS brings your menu across and sends phone orders straight to the
        kitchen. Without it, orders are still recorded here for staff to enter by hand.
      </p>

      <Panel title="Connections">
        {connections.isLoading ? (
          <SkeletonRows rows={2} />
        ) : rows.length === 0 ? (
          <EmptyState
            headline="No locations yet"
            hint="Add a location before connecting a point of sale."
          />
        ) : connected.length === 0 ? (
          <EmptyState
            headline="No POS connected"
            hint="Connect Clover below to sync your menu and push orders automatically."
          />
        ) : (
          <ul className="divide-y divide-rule">
            {connected.map((connection) => (
              <li key={connection.locationId} className="px-4 py-3.5">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-small font-medium text-ink">
                    {connection.provider === 'CLOVER' ? 'Clover' : connection.provider}
                  </span>
                  <span className="text-small text-slate">{connection.locationName}</span>
                  <Status value={connection.status} />

                  <div className="ml-auto flex items-center gap-2">
                    {connection.capabilities?.supportsMenuSync ? (
                      <Button
                        variant="secondary"
                        loading={syncState.isLoading}
                        onClick={() => runSync(connection)}
                      >
                        Sync menu
                      </Button>
                    ) : null}
                    <button
                      onClick={() => drop(connection)}
                      className="text-small text-slate hover:text-rust"
                    >
                      Disconnect
                    </button>
                  </div>
                </div>

                <p className="mt-1.5 text-small text-slate">
                  {connection.lastSyncAt
                    ? `Last synced ${formatDateTime(connection.lastSyncAt)} · ${
                        connection.lastSyncItemCount ?? 0
                      } items`
                    : 'Not synced yet'}
                  {connection.lastOrderSentAt
                    ? ` · last order ${formatDateTime(connection.lastOrderSentAt)}`
                    : ' · no order sent yet'}
                </p>

                {connection.ordersNeedingAttention > 0 ? (
                  <p className="mt-1 text-small text-rust">
                    {connection.ordersNeedingAttention} order
                    {connection.ordersNeedingAttention === 1 ? '' : 's'} need attention.
                  </p>
                ) : null}

                {connection.lastSyncError ? (
                  <p className="mt-1 rounded bg-rust-soft px-2.5 py-1.5 text-micro text-rust">
                    {connection.lastSyncError}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {notConnected.length > 0 ? (
        <Panel title="Locations without a POS">
          <ul className="divide-y divide-rule">
            {notConnected.map((row) => (
              <li
                key={row.locationId}
                className="flex flex-wrap items-center gap-3 px-4 py-3 text-small"
              >
                <span className="text-ink">{row.locationName}</span>
                <span className="ml-auto">
                  <Status value={row.status} />
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}

      <Panel title="Connect Clover">
        <div className="max-w-md space-y-4 px-4 py-4">
          <Field
            label="Merchant ID"
            value={form.merchantId}
            onChange={(event) => setForm({ ...form, merchantId: event.target.value })}
          />
          <Field
            label="API token"
            type="password"
            value={form.accessToken}
            onChange={(event) => setForm({ ...form, accessToken: event.target.value })}
          />
          <p className="text-small text-slate">
            Both are in your Clover dashboard under Setup, API tokens. The token is encrypted
            before it is stored and is never sent back to this page.
          </p>
          <Button loading={connectState.isLoading} disabled={!locationId} onClick={submit}>
            Connect
          </Button>
        </div>
      </Panel>

      <Panel title="Other systems">
        <ul className="divide-y divide-rule">
          {PROVIDERS.filter((provider) => !provider.available).map((provider) => (
            <li
              key={provider.code}
              className="flex items-center gap-3 px-4 py-3 text-small text-slate"
            >
              <span className="text-ink">{provider.name}</span>
              <span className="ml-auto">Not available yet</span>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}
